import { cn } from "shadcn/lib/utils";
import { useEffect, useState } from "react";

interface ExperienceGainNotificationProps {
  /** Amount of experience gained */
  expGained: number;
  /** Whether to show the notification */
  show: boolean;
  /** Callback when animation completes */
  onComplete?: () => void;
  /** Custom position */
  position?: "fixed" | "absolute";
  /** Custom styles */
  className?: string;
}

export default function ExperienceGainNotification({
  expGained,
  show,
  onComplete,
  position = "fixed",
  className
}: ExperienceGainNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setIsAnimating(true);

      // Complete animation after 2 seconds
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setIsVisible(false);
        onComplete?.();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "pointer-events-none z-[9999] flex items-center justify-center",
        position === "fixed" ? "fixed inset-0" : "absolute inset-0",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 px-4 py-2 text-white shadow-lg",
          "transform transition-all duration-700 ease-out",
          isAnimating
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-8 scale-95 opacity-0"
        )}
        style={{
          animation: isAnimating
            ? "experienceGain 2s ease-out forwards"
            : undefined
        }}
      >
        <span className="text-lg font-bold">⚡</span>
        <span className="text-sm font-bold">+{expGained} XP</span>
        <span className="text-lg font-bold">✨</span>
      </div>
    </div>
  );
}

// Add the keyframes to your global CSS or create a separate CSS file
export const experienceGainStyles = `
  @keyframes experienceGain {
    0% {
      transform: translateY(20px) scale(0.8);
      opacity: 0;
    }
    15% {
      transform: translateY(-10px) scale(1.1);
      opacity: 1;
    }
    30% {
      transform: translateY(0px) scale(1);
      opacity: 1;
    }
    70% {
      transform: translateY(0px) scale(1);
      opacity: 1;
    }
    100% {
      transform: translateY(-30px) scale(0.9);
      opacity: 0;
    }
  }
`;
